import { msg } from "../locales/translate";
import { DataProvider } from "badmfck-signal";
import { IExecutorResponse } from "badmfck-signal/dist/DataProvider";
import { GD } from "src/GD";
import type { ClientFinance } from "./clientListData";
import type { ClientListRequest } from "./clientListRequest";
import { loadClientDirectory } from "./clientDirectory";

//!TIDО:statuses enum;
export interface IClient {
	id: number;
	status_id: number;
	project_id: number;
	country: string;
	name: string;
	registration_number: string;
	user_id: number;
	merchants_cnt?: number;
	groups_cnt?: number;
	created_at?: number | string;
	updated_at?: number | string;
	status: string;
	processing_currency?: string;
	finance?: ClientFinance;
	payout?: {
		period: string;
		statement_day: string | null;
		cut_off_day: number | null;
		activated_from: string | null;
	} | null;
}

export class ClientService {
	//clients: DataProvider<{ projectID: number }, IClient[]> = new DataProvider({ cacheTime: 0 }, "Clients");

	statuses: DataProvider<{ projectID: number }, IClientStatus[]> = new DataProvider({ cacheTime: 0 }, "Statuses");

	groups: DataProvider<{ clientID: number }, IClientGroup[]> = new DataProvider({ cacheTime: 0 }, "Groups");

	constructor() {
		this.statuses.setExecutor(
			async () =>
				(await GD.REQ_API.request({
					method: "clients/status/list",
					data: {},
				})) as IExecutorResponse<IClientStatus[]>,
		);

		this.groups.setExecutor(async v => {
			return (await GD.REQ_API.request({
				method: "clients/group/list",
				data: {
					client_id: v?.clientID,
				},
			})) as IExecutorResponse<IClientGroup[]>;
		});

		GD.REQ_CLIENT.listener = async req => this.getClient(req);
		GD.REQ_EVENT.listener = async req => this.getEvent(req);
		GD.REQ_CLIENT_FILES.listener = async req => this.getClientFiles(req);
		GD.REQ_CLIENT_EVENTS.listener = async req => this.getClientEvents(req);
		GD.REQ_CLIENT_USERS.listener = async req => this.getClientUsers(req);
		GD.REQ_CLIENT_USER_CREATE.listener = async req => this.createClientUser(req);
		GD.REQ_CLIENT_USER_BLOCK.listener = async req => this.blockClientUser(req);
		GD.REQ_CLIENT_USER_UNBLOCK.listener = async req => this.unblockClientUser(req);
		GD.REQ_CLIENT_USER_REMOVE.listener = async req => this.removeClientUser(req);
		GD.REQ_CLIENT_SAVE.listener = async req => this.saveClient(req);
		GD.REQ_CLIENT_STATUS_CHANGE.listener = async req => this.changeStatus(req);

		GD.REQ_GROUP_SAVE.listener = async req => this.saveGroup(req);

		GD.REQ_GROUP.listener = async req => this.getGroup(req);

		//GD.B_CLIENTS.onRequest(async () => GD.B_CLIENTS.invoke(await this.clients.load()));

		GD.REQ_CLIENTS.listener = async req => this.getClients(req);

		GD.B_CLIENT_STATUSES.onRequest(async () => GD.B_CLIENT_STATUSES.invoke(await this.statuses.load()));

		GD.B_GROUPS.onRequest(async req => {
			if (req?.clientID !== undefined) {
				GD.B_GROUPS.invoke(await this.groups.load({ clientID: req?.clientID }));
			}
		});

		GD.REQ_GROUP_MERCHANTS.listener = async req => this.getGroupMerchants(req);

		GD.REQ_SETTINGS_GET.listener = async req => this.getSettings(req);
		GD.REQ_SETTINGS_SET.listener = async req => this.setSettings(req);

		GD.REQ_CLIENT_INFO_GET.listener = async req => this.getClientInfo(req);
		GD.REQ_CLIENT_INFO_SET.listener = async req => this.setClientInfo(req);
	}

	async getClientInfo(req: { clientId?: number }): Promise<IAPIResponse<IInfo>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		//!TODO: cache?;

		const info = await GD.REQ_API.request({
			method: "info/get",
			data: {
				target_id: req.clientId,
				target_type: "client",
			},
		});

		return info;
	}

	async setClientInfo(req: { clientID?: number; info: IInfo }): Promise<IAPIResponse<boolean>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		const clientInfoSave = await GD.REQ_API.request({
			method: "info/client/set",
			data: req.info,
		});

		return clientInfoSave;
	}

	//!TODO: remove from client manager;
	async getSettings(req: { clientId?: number }): Promise<IAPIResponse<IClientSettings>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		//!TODO: cache?;

		const settings = await GD.REQ_API.request({
			method: "settings/get",
			data: {
				target_id: req.clientId,
				target_type: "client",
				// id:-1
			},
		});

		return settings;
	}

	//!TODO: remove from client manager;
	async setSettings(req: { targetId?: number; settings: ISettingsData; id: number }): Promise<IAPIResponse<boolean>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		const merchantSettingsSave = await GD.REQ_API.request({
			method: "settings/set",
			data: req.settings,
			// data: {
			// 	target_id: req.clientID,
			// 	id: req.id,
			// 	target_type: "client",
			// 	data: req.settings,
			// },
		});

		// const clientInfoSave = await GD.REQ_API.request({
		// 	method: "info/client/set",
		// 	data: req.info,
		// });

		return merchantSettingsSave;
	}

	async getGroupMerchants(req: { groupID: number; clientID?: number }): Promise<IAPIResponse<IMerchantModel[]> | null> {
		//if (!req) return { error: { code: -1, message: "Invalid getAcquirer request" } };
		if (!req) return null;

		const merchants = await GD.REQ_API.request({
			method: "merchants/list",
			data: {
				group_id: req.groupID,
				client_id: req.clientID,
			},
		});

		return merchants;
	}

	//!TODO: check required fields and if this needs to be;
	generateNewGroup(clientId: number): IClientGroup {
		const res: IClientGroup = {
			client_id: clientId,
			domain: "",
			id: -1,
			name: "",
			risk: "high",
			test_user_name: "",
			test_user_password: "",
			created_at: +new Date(),
			updated_at: 0,
			status: "created",
			mastercard_enabled: false,
			visa_enabled: false,
			openbanking_enabled: false,
			website_url_address: "",
			description: "",
			support_email: "",
			support_phone: "",
		};
		return res;
	}

	async getGroup(req: { groupID: number; clientID?: number }): Promise<IAPIResponse<IClientGroup> | null> {
		//if (!req) return { error: { code: -1, message: "Invalid getAcquirer request" } };
		if (!req) return null;
		const groupID = req.groupID;

		if (groupID === -1 && req.clientID !== undefined) {
			return {
				data: this.generateNewGroup(req.clientID),
			};
		}

		//!TODO: load group;
		if (this.groups) {
			const f: IClientGroup | undefined = this.groups.getData()?.find(g => g.id === groupID);
			if (f) {
				return { data: f };
			}
		}

		//!TODO: method;
		const goup = await GD.REQ_API.request({
			method: "clients/group/get",
			data: {
				group_id: groupID,
				client_id: req.clientID,
			},
		});

		return goup;
	}

	async saveClient(req: IClient): Promise<IAPIResponse<IClient>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		//!TODO:;
		res = await GD.REQ_API.request({
			method: "clients/add",
			data: {
				id: req.id,
				project_id: req.project_id,
				country: req.country,
				name: req.name,
				registration_number: req.registration_number,
			},
		});
		if (res && !res.error) {
			GD.S_CLIENTS_UPDATE.invoke({});
		}
		return res;
	}

	async saveGroup(req: IClientGroup): Promise<IAPIResponse<IClientGroup>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		const { created_at, updated_at, ...data } = { ...req };

		res = await GD.REQ_API.request({
			method: "clients/group/add",
			data: data,
		});
		if (res && !res.error) {
			this.groups.reset();
			GD.B_GROUPS.invoke(await this.groups.load({ clientID: req.client_id }));
			//  GD.REQ_ACQUIRER.request({acquirerID:req.id})
		}
		return res;
	}

	async changeStatus(req: { client_id: number; status_id: number; reason: string }): Promise<IAPIResponse<boolean>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "clients/status/change",
			data: {
				client_id: req.client_id,
				status_id: req.status_id,
				reason: req.reason,
			},
		});

		if (res && !res.error) {
			const client = await GD.REQ_CLIENT.request({
				clientID: req.client_id,
			});
			//!TODO: dont chane local status;
			if (client && client.data) {
				GD.S_CLIENTS_UPDATE.invoke({});
			} else {
				//!TODO:;
			}
		}
		return res;
	}

	async getClientUsers(req: { clientID: number }): Promise<IAPIResponse<IClientUser[]>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "clients/users/list",
			data: {
				client_id: req.clientID,
			},
		});
		return res;
	}

	async createClientUser(req: { clientID: number; email: string; name?: string }): Promise<IAPIResponse<IClientUser>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "clients/user/create",
			data: {
				client_id: req.clientID,
				email: req.email,
				name: req.name,
			},
		});

		return res as IAPIResponse<IClientUser>;
	}

	async blockClientUser(req: { clientID: number; userID: number }): Promise<IAPIResponse> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "clients/user/block",
			data: {
				client_id: req.clientID,
				user_id: req.userID,
			},
		});

		return res;
	}

	async unblockClientUser(req: { clientID: number; userID: number }): Promise<IAPIResponse> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "clients/user/unblock",
			data: {
				client_id: req.clientID,
				user_id: req.userID,
			},
		});

		return res;
	}

	async removeClientUser(req: { clientID: number; userID: number }): Promise<IAPIResponse> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "clients/user/remove",
			data: {
				client_id: req.clientID,
				user_id: req.userID,
			},
		});

		return res;
	}

	async getClientEvents(req: { clientID: number }): Promise<IAPIResponse<IEvent[]>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "events/list",
			data: {
				target_id: req.clientID,
				target_type: "client",
			},
		});
		return res;
	}

	async getClientFiles(req: { clientID: number }): Promise<IAPIResponse<IClient>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "files/get",
			data: {
				id: req.clientID,
			},
		});
		return res;
	}

	async getClient(req: { clientID: number }): Promise<IAPIResponse<IClient>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		if (req.clientID === 0) {
			return { data: await this.generateNewClient() };
		}

		//!TODO: get from cache;

		// if (this.clients) {
		// 	const find = this.clients.getData()?.find(c => c.id === req.clientID);
		// 	if (find) {
		// 		return { data: find };
		// 	}
		// }

		res = await GD.REQ_API.request({
			method: "clients/get",
			data: {
				id: req.clientID,
			},
		});
		return res;
	}

	async getClients(req: ClientListRequest): Promise<IAPIResponse<IClient[]>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };
		try {
			return { data: await loadClientDirectory(req) };
		} catch (error) {
			if (error && typeof error === "object" && "code" in error && "message" in error) return { error: error as IError };
			return { error: { code: -1, message: error instanceof Error ? error.message : msg("couldNotLoadClients") } };
		}
	}

	async getEvent(req: { id: number }): Promise<IAPIResponse<IEvent>> {
		if (!req) return { error: { code: -1, message: msg("invalidRequest") } };

		let res: IAPIResponse = { error: { code: -1, message: msg("noData") } };

		res = await GD.REQ_API.request({
			method: "events/get",
			data: {
				id: req.id,
				project_id: (await GD.REQ_CURRENT_PROJECT.request())?.id,
			},
		});
		return res;
	}

	async generateNewClient(): Promise<IClient> {
		const client: IClient = {
			country: "",
			name: "",
			id: 0,
			project_id: (await GD.REQ_CURRENT_PROJECT.request())!.id,
			created_at: 0,
			merchants_cnt: 0,
			groups_cnt: 0,
			status_id: 0,
			updated_at: 0,
			user_id: 0,
			registration_number: "",
			status: "created",
		};
		return client;
	}
}
